import { useRef, useCallback, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { siteRoute } from '@/lib/domains';
import { SmartLink } from '@/components/shared/SmartLink';
import '@/styles/theapp-new.css';

const EASE = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, y = 24, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.9, ease: EASE }}
    >
      {children}
    </motion.div>
  );
};

const AccessCTA = ({ label = 'Request Early Access', variant = 'primary', testid }) => (
  <SmartLink
    to={siteRoute('/access')}
    className={variant === 'primary' ? 'tan-btn-primary' : 'tan-btn-ghost'}
    data-testid={testid}
  >
    {label}
  </SmartLink>
);

/* Arrow that fires a single 220ms nudge the first time it enters the viewport.
   Pure class toggle — no layout shift, hover interaction preserved. */
const ArrowOnce = ({ children = '→' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <span
      ref={ref}
      className={`tan-link-arrow${inView ? ' is-nudge' : ''}`}
      aria-hidden="true"
    >
      {children}
    </span>
  );
};

/* Kiss moment — signature interaction. Video starts paused; tapping the
   stage plays the video once and triggers the official kiss sound with a
   WebAudio bloom-in / resolve-out envelope. No autoplay, no loop, no hover
   sound — entirely intentional. */
const KissMoment = () => {
  const audioCtxRef    = useRef(null);
  const audioBufferRef = useRef(null);
  const bufferLoadRef  = useRef(null);
  const currentGainRef = useRef(null);
  const currentSrcRef  = useRef(null);
  const videoRef       = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);

  // Peak volume + envelope timings — "bloom in / resolve out". Do not raise
  // PEAK without explicit approval (spec: do NOT increase volume).
  const PEAK     = 0.55;
  const FADE_IN  = 0.135; // 135ms
  const FADE_OUT = 0.225; // 225ms

  // The video's first lip-to-camera contact lands at ~1.80s (frame analysis
  // of /images/tan-kiss.mp4). We schedule the audio so its bloom peak hits
  // this exact frame — i.e. start audio at (contact - fadeIn) into video.
  // That places the sound's loudest moment ON the contact frame, feeling
  // slightly anticipatory (never late), as if caused by the visual.
  const KISS_CONTACT_T = 1.80;
  const AUDIO_START_T  = Math.max(0, KISS_CONTACT_T - FADE_IN); // ~1.665s

  // Lazily fetch + decode the kiss sound. Called from the tap handler
  // so iOS Safari is happy to start the AudioContext.
  const ensureBuffer = useCallback(async () => {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      audioCtxRef.current = new Ctx();
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') {
      try { await ctx.resume(); } catch { /* iOS: ignored silently */ }
    }
    if (audioBufferRef.current) return audioBufferRef.current;
    if (!bufferLoadRef.current) {
      bufferLoadRef.current = (async () => {
        try {
          const r = await fetch('/audio/nymphe-kiss.mp3');
          const arr = await r.arrayBuffer();
          const buf = await new Promise((resolve, reject) => {
            const p = ctx.decodeAudioData(arr, resolve, reject);
            if (p && typeof p.then === 'function') p.then(resolve, reject);
          });
          audioBufferRef.current = buf;
          return buf;
        } catch {
          return null;
        }
      })();
    }
    return bufferLoadRef.current;
  }, []);

  // Fade-out and stop any currently playing source without audible cut.
  const resolveCurrent = (ctx, now) => {
    const gain = currentGainRef.current;
    const src  = currentSrcRef.current;
    if (!gain || !src) return;
    try {
      const v = gain.gain.value;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(v, now);
      gain.gain.linearRampToValueAtTime(0, now + FADE_OUT);
      src.stop(now + FADE_OUT + 0.01);
    } catch { /* swallow */ }
    currentGainRef.current = null;
    currentSrcRef.current  = null;
  };

  const playSound = useCallback(async () => {
    const buf = await ensureBuffer();
    const ctx = audioCtxRef.current;
    if (!ctx || !buf) return;

    const now = ctx.currentTime;
    resolveCurrent(ctx, now);

    const src = ctx.createBufferSource();
    src.buffer = buf;
    const gain = ctx.createGain();

    const dur = buf.duration;
    const fadeIn  = Math.min(FADE_IN,  dur * 0.4);
    const fadeOut = Math.min(FADE_OUT, dur * 0.5);

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(PEAK, now + fadeIn);
    gain.gain.setValueAtTime(PEAK, Math.max(now + fadeIn, now + dur - fadeOut));
    gain.gain.linearRampToValueAtTime(0, now + dur);

    src.connect(gain).connect(ctx.destination);
    src.start(now);
    src.stop(now + dur + 0.02);

    currentGainRef.current = gain;
    currentSrcRef.current  = src;
    src.onended = () => {
      if (currentSrcRef.current === src) {
        currentSrcRef.current  = null;
        currentGainRef.current = null;
      }
      try { src.disconnect(); gain.disconnect(); } catch { /* swallow */ }
    };
  }, [ensureBuffer]);

  // Ensure muted attribute is set at DOM level (Safari needs it to allow
  // inline silent playback even though we're not autoplaying).
  const videoCb = useCallback((node) => {
    videoRef.current = node;
    if (!node) return;
    node.muted = true;
    node.defaultMuted = true;
  }, []);

  // User intentionally taps the stage → play video ONCE from the start.
  // Audio is NOT triggered here; instead an rAF watcher below fires it at
  // the exact moment `video.currentTime` crosses AUDIO_START_T, so the
  // audio bloom peak lands on the contact frame (~1.80s).
  const onTapStage = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (!v.paused && !v.ended) return; // already playing — ignore
    try { v.currentTime = 0; } catch { /* swallow */ }
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});

    // Pre-warm the AudioContext + decode the buffer inside this user gesture
    // so playSound() later has zero cold-start latency. Fire-and-forget.
    ensureBuffer();

    // Watch video.currentTime; fire the kiss sound when it crosses the
    // (contact - fadeIn) threshold. Uses rAF for frame-accurate timing
    // regardless of decode/playback pipeline jitter.
    let fired = false;
    const tick = () => {
      const node = videoRef.current;
      if (!node || fired) return;
      if (node.ended || node.paused) return; // playback cancelled / ended
      if (node.currentTime >= AUDIO_START_T) {
        fired = true;
        playSound();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [ensureBuffer, playSound, AUDIO_START_T]);

  // Video lifecycle → keep React state in sync with the <video> element
  const onVideoPlay  = useCallback(() => setIsPlaying(true),  []);
  const onVideoEnded = useCallback(() => {
    setIsPlaying(false);
    const v = videoRef.current;
    if (v) {
      try { v.pause(); v.currentTime = 0; } catch { /* swallow */ }
    }
  }, []);
  const onVideoPause = useCallback(() => setIsPlaying(false), []);

  return (
    <section className="tan-section tan-kiss" data-testid="tan-kiss">
      <div className="tan-kiss-atmosphere" aria-hidden="true" />
      <div className="tan-container tan-kiss-grid">
        <div className="tan-kiss-text">
          <Reveal>
            <p className="tan-section-label">Signature Kiss</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="tan-h2">A softer way to show interest.</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="tan-kiss-body">
              Kiss is NYMPHÉ&rsquo;s signature interaction &mdash; a subtle way to show attraction
              without forcing a match, a message, or a performance. It gives someone a quiet
              signal that you&rsquo;re interested, while keeping the experience intentional,
              private, and controlled.
            </p>
          </Reveal>
        </div>

        <div className="tan-kiss-stage-wrap">
          <Reveal delay={0.32}>
            <button
              type="button"
              className={`tan-kiss-stage${isPlaying ? ' is-playing' : ''}`}
              data-testid="tan-kiss-stage"
              data-state={isPlaying ? 'playing' : 'paused'}
              aria-label={isPlaying ? 'Kiss moment playing' : 'Play Kiss moment'}
              aria-pressed={isPlaying}
              onClick={onTapStage}
            >
              <span className="tan-kiss-stage-halo" aria-hidden="true" />
              <video
                ref={videoCb}
                className="tan-kiss-video"
                muted
                playsInline
                preload="auto"
                aria-hidden="true"
                data-testid="tan-kiss-video"
                onPlay={onVideoPlay}
                onEnded={onVideoEnded}
                onPause={onVideoPause}
              >
                <source src="/images/tan-kiss.webm" type="video/webm" />
                <source src="/images/tan-kiss.mp4"  type='video/mp4; codecs="avc1.640028"' />
              </video>
              <span className="tan-kiss-stage-vignette" aria-hidden="true" />
              <span
                className="tan-kiss-play-glyph"
                data-testid="tan-kiss-play-glyph"
                aria-hidden="true"
              >
                <span className="tan-kiss-play-ring" />
                <span className="tan-kiss-play-dot">
                  <svg
                    viewBox="0 0 14 16"
                    width="26"
                    height="30"
                    aria-hidden="true"
                    className="tan-kiss-play-tri"
                  >
                    <path d="M1 1l12 7-12 7z" fill="currentColor" />
                  </svg>
                </span>
              </span>
            </button>
          </Reveal>
          <Reveal delay={0.42}>
            <p className="tan-kiss-micro">Tap to send the feeling.</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   PAGE DATA
   ────────────────────────────────────────────────────────────────────── */

const STEPS = [
  { n: '01', title: 'Create your private profile', body: 'Choose what you share and what stays hidden.' },
  { n: '02', title: 'Set your preferences',        body: 'Define identity, intention, and boundaries.' },
  { n: '03', title: 'Discover more intentionally', body: 'See profiles aligned with what you\u2019re actually here for.' },
  { n: '04', title: 'Connect with clarity',        body: 'Conversations begin where interest is mutual.' },
  { n: '05', title: 'Stay in control',             body: 'Adjust visibility or step away at any time.' },
];

const WHY_PILLARS = [
  { title: 'Curated, not crowded',  caption: 'The right people. The right energy.' },
  { title: 'Private by design',     caption: 'What you share stays yours.' },
  { title: 'Boundaries built in',   caption: 'Control lives inside every screen.' },
  { title: 'Respect as standard',   caption: 'The tone is set from the first message.' },
];

const APART = [
  { title: 'Verified encounters',     caption: 'Fewer unknowns. More confidence.'      },
  { title: 'Presence, not noise',     caption: 'Quality of attention over quantity of matches.' },
  { title: 'Energy-led discovery',    caption: 'Intention shapes what you see.'         },
  { title: 'Designed for discretion', caption: 'Privacy is the posture, not the setting.' },
];

/* FAQ */
const FAQ = [
  { q: 'What is NYMPHÉ?',           a: 'A private, adults-only connection platform.' },
  { q: 'Is it a dating app?',       a: 'It is a more intentional connection experience.' },
  { q: 'Who can join?',             a: 'Adults 18+ aligned with community standards.' },
  { q: 'Is my profile public?',     a: 'No.' },
  { q: 'Can I control visibility?', a: 'Yes.' },
  {
    q: 'How do I get access?',
    a: (
      <>
        <SmartLink
          to={siteRoute('/access')}
          className="tan-faq-link"
          data-testid="tan-faq-access-link"
        >
          Request early access
        </SmartLink>
        .
      </>
    ),
  },
];

/* ─────────────────────────────────────────────────────────────────────
   PAGE
   ────────────────────────────────────────────────────────────────────── */

const TheAppNewPage = () => {
  return (
    <div className="tan" data-testid="theapp-new-page">

      {/* ─── HERO ──────────────────────────────────────────────── */}
      <section className="tan-hero" data-testid="tan-hero">
        <div className="tan-hero-bg" aria-hidden="true">
          <div
            className="tan-hero-bg-image"
            style={{ backgroundImage: "url('/images/tan-hero-grid.webp')" }}
          />
          <div className="tan-hero-bg-overlay" />
          <div className="tan-hero-bg-vignette" />
          <div className="tan-hero-bg-glow tan-hero-bg-glow--a" />
          <div className="tan-hero-bg-glow tan-hero-bg-glow--b" />
        </div>
        <div className="tan-container tan-hero-inner">
          <Reveal>
            <p className="tan-eyebrow" data-testid="tan-hero-eyebrow">The App</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="tan-hero-headline" data-testid="tan-hero-headline">
              Find your kind,<br />
              <span className="tan-hero-em">privately.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.24}>
            <p className="tan-hero-body">
              NYMPHÉ is a private, adults-only connection app for trans women, femmes, and
              respectful admirers who value discretion, consent, and genuine recognition.
            </p>
          </Reveal>
          <Reveal delay={0.34}>
            <p className="tan-hero-sub">
              Curated discovery. Thoughtful boundaries. A quieter way to connect.
            </p>
          </Reveal>
          <Reveal delay={0.46}>
            <div className="tan-hero-cta-row">
              <AccessCTA testid="tan-hero-primary-cta" />
              <a href="#how-it-works" className="tan-link-quiet" data-testid="tan-hero-secondary-cta">
                See how it works
                <span className="tan-link-arrow" aria-hidden="true">→</span>
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.6}>
            <ul className="tan-trust-strip" data-testid="tan-trust-strip">
              <li>18+ only</li><li><span aria-hidden="true">·</span></li>
              <li>Privacy-first</li><li><span aria-hidden="true">·</span></li>
              <li>Curated discovery</li><li><span aria-hidden="true">·</span></li>
              <li>Respect required</li>
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ─── DISCOVERY REVEAL (a quiet glimpse into the community) ─── */}
      <section className="tan-reveal" data-testid="tan-reveal" aria-label="A glimpse inside">
        <div className="tan-reveal-overlay" aria-hidden="true" />
        <div className="tan-reveal-vignette" aria-hidden="true" />
        <div className="tan-reveal-fade tan-reveal-fade--top" aria-hidden="true" />
        <div className="tan-reveal-fade tan-reveal-fade--bottom" aria-hidden="true" />

      </section>

      {/* ─── WHAT NYMPHÉ IS ────────────────────────────────────── */}
      <section className="tan-section tan-what" data-testid="tan-what">
        <div className="tan-container tan-narrow">
          <Reveal><p className="tan-section-label">What NYMPHÉ is</p></Reveal>
          <Reveal delay={0.1}>
            <p className="tan-lede">
              NYMPHÉ is designed for adults who want something more intentional than the noise of
              mainstream apps. It is a private connection space built around discretion, mutual
              recognition, and a more considered way to discover each other.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="tan-lede tan-lede--muted">
              The experience feels curated rather than crowded &mdash; with more control, stronger
              boundaries, and a tone that values respect from the beginning.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── WHO IT'S FOR ──────────────────────────────────────── */}
      <section className="tan-section tan-who" data-testid="tan-who">
        <div className="tan-container">
          <Reveal><h2 className="tan-h2">Who it&rsquo;s for.</h2></Reveal>
          <div className="tan-who-grid">
            <Reveal delay={0.1}>
              <article className="tan-who-card">
                <p className="tan-who-label">For trans women and femmes</p>
                <p className="tan-who-body">Be seen without being overexposed. Control who sees you, and when.</p>
              </article>
            </Reveal>
            <Reveal delay={0.22}>
              <article className="tan-who-card">
                <p className="tan-who-label">For respectful admirers</p>
                <p className="tan-who-body">Come correct. This space values discretion, consent, and awareness.</p>
              </article>
            </Reveal>
          </div>
          <Reveal delay={0.34}>
            <p className="tan-who-support">The space works better when everyone enters with intention.</p>
          </Reveal>
        </div>
      </section>

      {/* ─── PRIMARY SHOWCASE · Phone UI (discovery grid) ──────── */}
      <section className="tan-section tan-showcase tan-showcase--primary" data-testid="tan-showcase-primary">
        <div className="tan-container tan-showcase-inner">
          <Reveal className="tan-showcase-copy">
            <p className="tan-section-label">Inside the app</p>
            <h2 className="tan-h2">Curated faces,<br/>never a feed.</h2>
            <p className="tan-showcase-body">
              A quiet grid of nearby presence. No infinite scroll. No metrics. Just a considered
              handful of people — verified, close, and open.
            </p>
          </Reveal>
          <Reveal delay={0.18} className="tan-showcase-visual">
            <div className="tan-showcase-phone" data-testid="tan-showcase-phone">
              <div className="tan-showcase-phone-halo" aria-hidden="true" />
              <img
                className="tan-showcase-phone-img"
                src="/images/the-app-access-grid-ui-polished.png"
                alt="NYMPHÉ access screen on iPhone showing a profile grid background with Apple, Google, and email sign-in options."
                loading="lazy"
                decoding="async"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── BROWSE → REFINE · atmospheric breath band ─────────── */}
      <section className="tan-breath" data-testid="tan-breath-browse-refine" aria-hidden="true">
        <div className="tan-breath-overlay" />
        <div className="tan-breath-vignette" />
        <div className="tan-breath-fade tan-breath-fade--top" />
        <div className="tan-breath-fade tan-breath-fade--bottom" />
      </section>

      {/* ─── SUPPORTING · Filter UI (refine step in the flow) ──── */}
      <section className="tan-section tan-showcase tan-showcase--filters" data-testid="tan-showcase-filters">
        <div className="tan-container tan-showcase-inner tan-showcase-inner--rl">
          <Reveal className="tan-showcase-visual tan-showcase-visual--small">
            <div className="tan-showcase-filter" data-testid="tan-showcase-filter">
              <div className="tan-showcase-filter-halo" aria-hidden="true" />
              <img
                className="tan-showcase-filter-img"
                src="/images/the-app-filters-ui-polished.png"
                alt="NYMPHÉ filters screen on iPhone showing age, distance, online, verified, profile photo, and bio controls."
                loading="lazy"
                decoding="async"
              />
            </div>
          </Reveal>
          <Reveal delay={0.18} className="tan-showcase-copy tan-showcase-copy--right">
            <p className="tan-section-label">Refine what you see</p>
            <h2 className="tan-h2">Discovery, narrowed<br/>intentionally.</h2>
            <p className="tan-showcase-body">
              Age, distance, presence, verification &mdash; shaped quietly until the room feels
              right. No loud mechanics. Just a calmer way to narrow the field.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── HOW IT WORKS ──────────────────────────────────────── */}
      <section id="how-it-works" className="tan-section tan-how" data-testid="tan-how">
        <div className="tan-container">
          <Reveal><p className="tan-section-label">How it works</p></Reveal>
          <Reveal delay={0.1}><h2 className="tan-h2">Five quiet steps inside.</h2></Reveal>
          <ol className="tan-steps">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={0.14 + i * 0.08}>
                <li className="tan-step" data-testid={`tan-step-${s.n}`}>
                  <span className="tan-step-num" aria-hidden="true">{s.n}</span>
                  <div className="tan-step-body">
                    <p className="tan-step-title">{s.title}</p>
                    <p className="tan-step-caption">{s.body}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* ─── CINEMATIC BRIDGE · emotional pause after the 5-step journey
           (uses the existing tan-closing image; copy & visual unchanged,
           with the --bridge modifier adding generous breathing room). ── */}
      <section
        className="tan-closing tan-closing--bridge"
        data-testid="tan-closing"
        aria-labelledby="tan-closing-h"
      >
        <div
          className="tan-closing-image"
          style={{ backgroundImage: "url('/images/tan-closing.webp')" }}
          aria-hidden="true"
        />
        <div className="tan-closing-scrim" aria-hidden="true" />
        <div className="tan-closing-vignette" aria-hidden="true" />
        <div className="tan-container tan-closing-inner">
          <Reveal>
            <h2 id="tan-closing-h" className="tan-closing-h">
              Not everything needs to be said.
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="tan-closing-body">
              Some signals are felt &mdash; quietly, intentionally.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── WHY IT FEELS DIFFERENT ────────────────────────────── */}
      <section className="tan-section tan-why" data-testid="tan-why">
        <div className="tan-why-bg" aria-hidden="true" />
        <div className="tan-container">
          <Reveal><p className="tan-section-label tan-section-label--center">Why it feels different</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="tan-h2 tan-h2--center">A quieter atmosphere,<br/>by design.</h2>
          </Reveal>
          <div className="tan-why-grid">
            {WHY_PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={0.2 + i * 0.08}>
                <div className="tan-why-card" data-testid={`tan-why-${i + 1}`}>
                  <p className="tan-why-title">{p.title}</p>
                  <p className="tan-why-caption">{p.caption}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.56}>
            <p className="tan-why-support">Some connections deserve a quieter entry.</p>
          </Reveal>
        </div>
      </section>

      {/* ─── KISS MOMENT (first intentional action) ────────────── */}
      <KissMoment />

      {/* ─── PRIVATE BEAT · cinematic pause between Kiss and the
           profile moment. Image carries the atmosphere of private,
           intentional interaction; copy sits in safe negative space. ── */}
      <section
        className="tan-private-beat"
        data-testid="tan-private-beat"
        aria-label="Private by design"
      >
        <picture className="tan-private-beat-picture" aria-hidden="true">
          <img
            src="/assets/theapp-mid-hero.png"
            alt=""
            loading="lazy"
            decoding="async"
            className="tan-private-beat-img"
            data-testid="tan-private-beat-image"
          />
        </picture>
        <div className="tan-private-beat-tint" aria-hidden="true" />
        <div className="tan-private-beat-fade" aria-hidden="true" />
        <div className="tan-private-beat-inner">
          <p className="tan-section-label">A quieter way to connect</p>
          <h2 className="tan-private-beat-head">Private by design.</h2>
          <p className="tan-private-beat-sub">
            What you explore, who you notice, and how you move stays yours.
          </p>
        </div>
      </section>

      {/* ─── SECONDARY MOMENT · Emma profile card (used once) ──── */}
      <section className="tan-section tan-showcase tan-showcase--secondary" data-testid="tan-showcase-secondary">
        <div className="tan-container tan-showcase-inner tan-showcase-inner--rl">
          <Reveal className="tan-showcase-visual tan-showcase-visual--small">
            <div className="tan-showcase-card" data-testid="tan-showcase-card">
              <div className="tan-showcase-card-halo" aria-hidden="true" />
              <img
                className="tan-showcase-card-img"
                src="/images/the-app-profile-action-ui-polished.png"
                alt="NYMPHÉ profile screen on iPhone showing Emma, distance, activity status, and Message, Kiss, and Hotlist actions."
                loading="lazy"
                decoding="async"
              />
            </div>
          </Reveal>
          <Reveal delay={0.18} className="tan-showcase-copy tan-showcase-copy--right">
            <p className="tan-section-label">A single considered moment</p>
            <h2 className="tan-h2">A profile,<br/>not a pitch.</h2>
            <p className="tan-showcase-body">
              Each profile is a quiet invitation — image, proximity, presence. Nothing louder than
              needed. A Kiss is a considered signal, not a swipe.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── WHAT SETS NYMPHÉ APART ────────────────────────────── */}
      <section className="tan-section tan-apart" data-testid="tan-apart">
        <div className="tan-container">
          <Reveal><p className="tan-section-label">What sets NYMPHÉ apart</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="tan-h2">Not more features.<br/>A better atmosphere.</h2>
          </Reveal>
          <div className="tan-apart-grid">
            {APART.map((p, i) => (
              <Reveal key={p.title} delay={0.2 + i * 0.08}>
                <article className="tan-apart-card" data-testid={`tan-apart-${i + 1}`}>
                  <span className="tan-apart-dot" aria-hidden="true" />
                  <p className="tan-apart-title">{p.title}</p>
                  <p className="tan-apart-caption">{p.caption}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.56}>
            <p className="tan-why-support">
              This is not about more features. It is about a better atmosphere.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── MAP / LOCATION STORYTELLING ───────────────────────── */}
      <section className="tan-section tan-map" data-testid="tan-map">
        <div className="tan-container tan-map-grid">
          <Reveal className="tan-map-copy">
            <p className="tan-section-label">Location, on your terms</p>
            <h2 className="tan-h2">Share location,<br/>on your terms.</h2>
            <p className="tan-map-body">
              Connection does not need to feel uncertain. When it feels right, you can choose to
              share your location &mdash; precisely, temporarily, and entirely on your terms.
            </p>
            <p className="tan-map-body tan-map-body--muted">
              No pressure. No assumptions. Just clarity when it matters.
            </p>
          </Reveal>
          <Reveal delay={0.18} className="tan-map-visual">
            <div className="tan-map-frame">
              <video
                className="tan-map-video"
                src="/images/map-melbourne-web.mp4"
                poster="/images/chat-map-still.jpg"
                autoPlay muted loop playsInline preload="metadata"
                aria-hidden="true"
              />
              <div className="tan-map-glow" aria-hidden="true" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── MESSAGE EXAMPLE · conversation with context ───────── */}
      <section className="tan-section tan-msg" data-testid="tan-msg">
        <div className="tan-container tan-msg-grid">
          <Reveal className="tan-msg-visual">
            <div className="tan-msg-frame">
              <img
                src="/images/the-app-message-example-ui.png"
                alt="NYMPHÉ message example on iPhone showing a private conversation, location card, typing indicator, and message input."
                loading="lazy"
                decoding="async"
                className="tan-msg-img"
                data-testid="tan-msg-img"
              />
            </div>
          </Reveal>
          <Reveal delay={0.18} className="tan-msg-copy">
            <p className="tan-section-label">Messaging</p>
            <h2 className="tan-h2">Conversation,<br/>with context.</h2>
            <p className="tan-msg-body">
              Share more than words when the moment feels right &mdash; from messages to location cues, every interaction stays clear, controlled, and intentional.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── MEMBERSHIP (minimal mention + link to /membership) ── */}
      <section className="tan-section tan-membership-lite" data-testid="tan-membership">
        <div className="tan-container tan-narrow tan-center">
          <Reveal>
            <p className="tan-section-label">Membership</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="tan-h2 tan-h2--center tan-membership-lite-h">
              Membership, on your terms.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="tan-membership-lite-body">
              Unlock deeper access, visibility, and control &mdash; when you&rsquo;re ready.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <SmartLink
              to={siteRoute('/membership')}
              className="tan-membership-lite-link"
              data-testid="tan-membership-link"
            >
              See membership options
              <ArrowOnce />
            </SmartLink>
          </Reveal>
        </div>
      </section>

      {/* ─── BOTTOM VIDEO HERO · the emotional result of NYMPHÉ ──
           Sits directly above the final CTA. This is the "where it leads"
           moment — autoplay, muted, looped, no controls, no UI. */}
      <section
        className="tan-bottom-video-hero"
        data-testid="tan-bottom-video-hero"
        aria-labelledby="tan-bottom-video-h"
      >
        <video
          className="tan-bottom-video-media"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          data-testid="tan-bottom-video"
        >
          <source src="/videos/the-app-bottom-result-hero.webm" type="video/webm" />
          <source src="/videos/the-app-bottom-result-hero.mp4" type="video/mp4" />
        </video>
        <div className="tan-bottom-video-scrim" aria-hidden="true" />
        <div className="tan-bottom-video-vignette" aria-hidden="true" />
        <div className="tan-container tan-bottom-video-inner">
          <Reveal>
            <h2 id="tan-bottom-video-h" className="tan-bottom-video-h">
              This is where intention leads.
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="tan-bottom-video-body">
              From discovery to connection, every step is designed to feel clear, private, and real.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── FINAL CTA (with closing visual) ───────────────────── */}
      <section className="tan-section tan-final" data-testid="tan-final-cta">
        <div className="tan-final-glow" aria-hidden="true" />
        <div className="tan-final-door" aria-hidden="true">
          <div className="tan-final-door-line" />
          <div className="tan-final-door-line" />
          <div className="tan-final-door-line" />
        </div>
        <div className="tan-container tan-narrow tan-final-inner">
          <Reveal><h2 className="tan-final-h">A quieter way in.</h2></Reveal>
          <Reveal delay={0.12}>
            <p className="tan-final-body">
              NYMPHÉ is not designed for everyone. It is designed for the right energy &mdash;
              private, intentional, and built around mutual respect.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="tan-final-cta">
              <AccessCTA testid="tan-final-primary-cta" />
            </div>
          </Reveal>
          <Reveal delay={0.36}>
            <p className="tan-final-micro">
              Requesting access is free. This does not create a public profile.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── FAQ ───────────────────────────────────────────────── */}
      <section className="tan-section tan-faq" data-testid="tan-faq">
        <div className="tan-container tan-narrow">
          <Reveal><p className="tan-section-label">Frequently asked</p></Reveal>
          <Reveal delay={0.1}><h2 className="tan-h2">Quick clarity.</h2></Reveal>
          <dl className="tan-faq-list" data-testid="tan-faq-list">
            {FAQ.map((row, i) => (
              <Reveal key={row.q} delay={0.16 + i * 0.05}>
                <div className="tan-faq-row" data-testid={`tan-faq-${i + 1}`}>
                  <dt className="tan-faq-q">{row.q}</dt>
                  <dd className="tan-faq-a">{row.a}</dd>
                </div>
              </Reveal>
            ))}
          </dl>
        </div>
      </section>
    </div>
  );
};

export default TheAppNewPage;
